import { Box, Ellipsis, Flex, Image, Text } from '@oplog/express';
import { Resource } from '@oplog/resource-redux';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ResourceType } from '../../../../models';
import {
  ListCarriersOutputDTO,
  PrintApprovedCargoPackageCarrierLabelOutputDTO,
  PrintCargoPackageCarrierLabelOutputDTO,
} from '../../../../services/swagger';
import { StoreState } from '../../../../store/initState';
import { StatusEnum } from './BarcodeIcon';

export interface IPackageScanInfo {
  status: StatusEnum;
  response: Resource<PrintCargoPackageCarrierLabelOutputDTO | PrintApprovedCargoPackageCarrierLabelOutputDTO>;
}

const intlKey = 'TouchScreen.SlamStation.ScanBox';

const PackageScanInfo: FC<IPackageScanInfo> = ({ status, response }) => {
  const { t } = useTranslation();
  const listCarriersResponse: Resource<ListCarriersOutputDTO> = useSelector(
    (state: StoreState) => state.resources[ResourceType.ListCarriers]
  );

  // Onayli siparis istasyonu ayni bileseni kendi DTO'suyla kullaniyor ve onda bu alan yok; okuma
  // bu yuzden alan bazinda daraltiliyor, DTO tipini ayirmak iki istasyonu da degistirmeyi gerektirirdi.
  const isCargoLabelAlreadyPrintedAtPacking =
    (response?.data as PrintCargoPackageCarrierLabelOutputDTO | undefined)?.isCargoLabelAlreadyPrintedAtPacking === true;

  switch (status) {
    case StatusEnum.Initial:
      return (
        <Text mt={64} fontSize={24} color="palette.slate_dark" fontWeight={500} lineHeight="large">
          {t(`${intlKey}.ScanPackage`)}
        </Text>
      );
    case StatusEnum.Success:
      return (
        <>
          <Text fontSize={14} color="palette.hardGreen" fontWeight={500} lineHeight="xxLarge">
            {t(`${intlKey}.ScanSuccess`)}
          </Text>
          <Text mt={16} fontSize={14} color="palette.slate_dark" fontWeight={500}>
            {t(`${intlKey}.OrderNumber`)}
          </Text>
          <Flex
            fontSize={26}
            fontWeight={500}
            color="palette.slate_dark"
            mt={8}
            width={1}
            lineHeight="xLarge"
            textAlign="center"
          >
            <Ellipsis maxWidth={1000}>{response?.data?.salesOrderReferenceNumber || ''}</Ellipsis>
          </Flex>
          <Box
            my={11}
            backgroundImage="linear-gradient(to right, #edf2f7 50%, #718096 0%)"
            backgroundPosition="bottom"
            backgroundSize="20px 1px"
            backgroundRepeat="repeat-x"
            height={1}
            width={1}
          />
          <Text mt={11} fontSize={14} color="palette.slate_dark" fontWeight={500}>
            {t(`${intlKey}.PackageNumber`)}
          </Text>
          <Text mt={8} fontSize={26} color="palette.slate_dark" fontWeight={500} lineHeight="small">
            {response?.data?.cargoPackageLabel || ''}
          </Text>
          <Flex
            height={44}
            bg="palette.snow_lighter"
            justifyContent="center"
            alignItems="center"
            borderRadius="lg"
            mt={16}
          >
            <Image
              src={
                listCarriersResponse.data?.carriers?.find(
                  carrier => carrier.name === response?.data?.cargoCarrier || ''
                )?.enabledLogoUrl || ''
              }
              alt={response?.data?.cargoCarrier || 'Carrier Logo'}
              height={44}
              width={44}
              my={8}
              mx={16}
              borderRadius="lg"
            />
            <Text fontSize={16} color="palette.hardBlue_darker" fontWeight={600} mr={16}>
              {response?.data?.cargoCarrier || ''}
            </Text>
          </Flex>
          {isCargoLabelAlreadyPrintedAtPacking && (
            <Text mt={16} fontSize={16} color="palette.hardBlue_darker" textAlign="center" px={16} lineHeight="medium">
              {t(`${intlKey}.CargoLabelAlreadyPrintedAtPacking`)}
            </Text>
          )}
        </>
      );
    case StatusEnum.WrongBarcode:
      return (
        <>
          <Text mt={64} fontSize={26} color="palette.red_darker" fontWeight={500} lineHeight="large">
            {t(`${intlKey}.WrongBarcode`)}
          </Text>
          <Text mt={16} fontSize={16} color="palette.slate_dark" lineHeight="xxLarge">
            {t(`${intlKey}.WrongBarcodeSubtext`)}
          </Text>
        </>
      );
    case StatusEnum.Cancelled:
      return (
        <>
          <Text mt={64} fontSize={26} color="palette.hardRed" fontWeight={500} lineHeight="large">
            {t(`${intlKey}.OrderCancelled`)}
          </Text>
          <Text mt={16} fontSize={16} color="palette.slate_dark" textAlign="center" px={44} lineHeight="medium">
            {t(`${intlKey}.OrderCancelledSubtext`)}
          </Text>
        </>
      );
    case StatusEnum.ShipmentFailure:
      return (
        <>
          <Text mt={64} fontSize={26} color="palette.hardOrange" fontWeight={500} lineHeight="large">
            {t(`${intlKey}.OrderProblematic`)}
          </Text>
          <Text mt={16} fontSize={16} color="palette.slate_dark" textAlign="center" px={44} lineHeight="medium">
            {t(`${intlKey}.OrderProblematicSubtext`)}
          </Text>
        </>
      );
    case StatusEnum.Loading:
      return (
        <Text
          mt={64}
          fontSize={26}
          color="palette.slate_dark"
          fontWeight={500}
          lineHeight="large"
          data-testid="loading"
        >
          {t(`${intlKey}.Loading`)}
        </Text>
      );
    default:
      return null;
  }
};

export default PackageScanInfo;
